import { createPinia, defineStore } from 'pinia';
import { ref } from 'vue';
import { Requests } from '@tools/requests';
import {
  URL_PAGE_SPACE_MEDIAS_UPLOAD,
  URL_PAGE_SPACE_MEDIA_DELETE,
  URL_SELLER_BRAND_UPDATE_PAGE_SPACE,
} from '../routes/routes';
import { Logger } from '../logger/logger';

export const useAddressStore = defineStore('address', () => {
  const address = ref(null);
  const images = ref([]);
  const allowedImageFormat = ref('image/*');
  const isUploading = ref(false);

  const initialPageSpace = ref(null);
  const currentPageSpace = ref(null);
  const completePageSpaceUrl = ref(null);

  const setAllowedImageFormat = (formats) => {
    allowedImageFormat.value = formats;
  };

  const upload = async (image) => {
    if (!currentPageSpace.value) {
      return null;
    }

    const endpoint = URL_PAGE_SPACE_MEDIAS_UPLOAD.replace('{id}', currentPageSpace.value.id);

    const lastIndex = images.value.push(image) - 1;

    const formData = new FormData();
    formData.append('file', image.file);
    formData.append('order', lastIndex);

    try {
      const response = await Requests.post(endpoint, formData);

      if (!response.data?.uuid) {
        throw new Error('Error when uploading file');
      }

      const media = {
        uuid: response.data.uuid,
        pageSpaceMediaId: response.data.pageSpaceMediaId,
        order: lastIndex,
        src: image.src,
      };

      images.value.splice(lastIndex, 1, media);
      return media;
    } catch (error) {
      Logger.error(error);
      return null;
    }
  };

  const deleteMedia = async (index) => {
    const image = images.value[index];

    images.value.splice(index, 1);

    const endpoint = URL_PAGE_SPACE_MEDIA_DELETE
      .replace('{id}', currentPageSpace.value.id)
      .replace('{pageSpaceMediaId}', image.pageSpaceMediaId);

    try {
      await Requests.delete(endpoint);
    } catch (error) {
      Logger.error(error);
      images.value.splice(index, 0, image);
    }
  };

  const initPageSpaceMedia = (orderedMedias) => {
    const medias = orderedMedias.map((orderedMedia) => ({
      uuid: orderedMedia.media.uuid,
      pageSpaceMediaId: orderedMedia.id,
      order: orderedMedia.display_order,
    }));

    medias.sort((a, b) => a.order - b.order);

    images.value = medias;
  };

  const setAddress = (newAddress) => {
    address.value = newAddress;
  };

  const setInitialPageSpace = (newPageSpace) => {
    initialPageSpace.value = newPageSpace;
  };

  const setCurrentPageSpace = (newCurrentPageSpace) => {
    currentPageSpace.value = newCurrentPageSpace;

    if (currentPageSpace.value?.ordered_page_space_medias) {
      initPageSpaceMedia(currentPageSpace.value.ordered_page_space_medias);
    }
  };

  const setCurrentPageSpaceValues = (values) => {
    currentPageSpace.value = { ...currentPageSpace.value, ...values };
  };

  const setCompletePageSpaceUrl = (newCompletePageSpaceUrl) => {
    completePageSpaceUrl.value = newCompletePageSpaceUrl;
  };

  const updatePageSpace = async (pageSpace, payload) => {
    const url = URL_SELLER_BRAND_UPDATE_PAGE_SPACE.replace('{pageSpaceId}', pageSpace.id);

    try {
      const response = await Requests.put(url, payload);

      return JSON.parse(response.data);
    } catch (error) {
      Logger.error(error);
      return null;
    }
  };

  const persistMediaDisplayOrder = async () => {
    const url = URL_PAGE_SPACE_MEDIAS_UPLOAD.replace('{id}', currentPageSpace.value.id);

    const medias = images.value.map((media, index) => ({
      uuid: media.uuid,
      display_order: index,
      id: media.pageSpaceMediaId,
    }));

    try {
      await Requests.put(url, { medias });
    } catch (error) {
      Logger.error(error);
    }

    return null;
  };

  return {
    address,
    initialPageSpace,
    currentPageSpace,
    completePageSpaceUrl,
    images,
    isUploading,
    allowedImageFormat,
    setAddress,
    setInitialPageSpace,
    setCurrentPageSpace,
    setCompletePageSpaceUrl,
    setCurrentPageSpaceValues,
    updatePageSpace,
    upload,
    deleteMedia,
    persistMediaDisplayOrder,
    setAllowedImageFormat,
  };
});
export const piniaAddress = createPinia();
